/* eslint-disable import/prefer-default-export */
/**
 * Demo implementation of `ReaderDataService`, backed by localStorage.
 *
 * Deliberately outside `shell/`: the shell is what SPEC Phase 1 extracts as a
 * package, and it must not carry a storage choice with it. Picking the
 * implementation is the application's job — this file is that pick for the POC,
 * and the GraphQL client will be its replacement.
 *
 * The stored shape is the real v1 envelope, and the backend keeps it as an
 * opaque `highlightData: JSON!`, so swapping this out moves the transport and
 * nothing else.
 */

import {
  DEFAULT_DECK_ID,
  DEFAULT_DECK_NAME,
  type ReaderDataService,
  type StoredDeck,
  type StoredFlashcard,
  type StoredFlashcardInput,
  type StoredFlashcardPatch,
  type StoredBookmark,
  type StoredBookmarkInput,
  type StoredHighlight,
  type StoredHighlightInput,
  type StoredHighlightPatch,
} from './shell/data-service';

const keyFor = (documentId: string) => `reader-poc:highlights:${documentId}`;
const deckKeyFor = (documentId: string) => `reader-poc:decks:${documentId}`;
const cardKeyFor = (documentId: string) =>
  `reader-poc:flashcards:${documentId}`;
const bookmarkKeyFor = (documentId: string) =>
  `reader-poc:bookmarks:${documentId}`;

const mintId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

const readList = <T>(key: string): T[] => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
};

const writeList = <T>(key: string, items: T[]): void => {
  window.localStorage.setItem(key, JSON.stringify(items));
};

/**
 * Decks, with the default one always present and always first.
 *
 * Production synthesises it client-side rather than storing it, and filters
 * stored decks that reuse the name so exactly one exists. Same rules here, or
 * the two readers would disagree about the same data.
 */
const readDecks = (documentId: string): StoredDeck[] => [
  { id: DEFAULT_DECK_ID, name: DEFAULT_DECK_NAME },
  ...readList<StoredDeck>(deckKeyFor(documentId)).filter(
    (deck) => deck.id !== DEFAULT_DECK_ID && deck.name !== DEFAULT_DECK_NAME
  ),
];

const read = (documentId: string): StoredHighlight[] => {
  try {
    const raw = window.localStorage.getItem(keyFor(documentId));
    return raw ? (JSON.parse(raw) as StoredHighlight[]) : [];
  } catch {
    // Unreadable or malformed storage behaves as empty rather than throwing:
    // a corrupt demo key should not take the reader down with it.
    return [];
  }
};

const write = (documentId: string, highlights: StoredHighlight[]): void => {
  window.localStorage.setItem(keyFor(documentId), JSON.stringify(highlights));
};

export const localStorageDataService: ReaderDataService = {
  async getHighlights(documentId: string): Promise<StoredHighlight[]> {
    return read(documentId);
  },

  async addHighlight(
    documentId: string,
    input: StoredHighlightInput
  ): Promise<StoredHighlight> {
    // Client-minted, matching the backend: `courseMaterialHighlightManage`
    // takes `highlightId` as an argument rather than assigning one.
    const highlight: StoredHighlight = {
      id: `hl-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      createdAt: new Date().toISOString(),
      envelope: input.envelope,
      color: input.color,
      body: input.body,
    };
    write(documentId, [...read(documentId), highlight]);
    return highlight;
  },

  async updateHighlight(
    documentId: string,
    id: string,
    patch: StoredHighlightPatch
  ): Promise<StoredHighlight> {
    const highlights = read(documentId);
    const index = highlights.findIndex((highlight) => highlight.id === id);
    if (index < 0) {
      // Editing something that is no longer stored is a real failure, not a
      // no-op to paper over: the caller is holding a stale list and the user
      // would watch their words vanish on the next load.
      throw new Error(`No highlight ${id} on ${documentId}`);
    }

    const updated = { ...highlights[index], ...patch };
    highlights[index] = updated;
    write(documentId, highlights);
    return updated;
  },

  async removeHighlight(documentId: string, id: string): Promise<void> {
    write(
      documentId,
      read(documentId).filter((highlight) => highlight.id !== id)
    );
  },

  async getDecks(documentId: string): Promise<StoredDeck[]> {
    return readDecks(documentId);
  },

  async addDeck(documentId: string, name: string): Promise<StoredDeck> {
    const deck: StoredDeck = { id: mintId('deck'), name: name.trim() };
    const stored = readList<StoredDeck>(deckKeyFor(documentId));
    writeList(deckKeyFor(documentId), [...stored, deck]);
    return deck;
  },

  async renameDeck(
    documentId: string,
    id: string,
    name: string
  ): Promise<StoredDeck> {
    if (id === DEFAULT_DECK_ID) {
      // It is synthesised, not stored, so a rename would silently vanish on
      // the next read. Fail loudly rather than pretend.
      throw new Error('The default deck cannot be renamed');
    }
    const stored = readList<StoredDeck>(deckKeyFor(documentId));
    const index = stored.findIndex((deck) => deck.id === id);
    if (index < 0) throw new Error(`No deck ${id} on ${documentId}`);

    const updated = { ...stored[index], name: name.trim() };
    stored[index] = updated;
    writeList(deckKeyFor(documentId), stored);
    return updated;
  },

  async removeDeck(documentId: string, id: string): Promise<void> {
    if (id === DEFAULT_DECK_ID) {
      throw new Error('The default deck cannot be removed');
    }
    writeList(
      deckKeyFor(documentId),
      readList<StoredDeck>(deckKeyFor(documentId)).filter(
        (deck) => deck.id !== id
      )
    );
    // Cascade, as production does — otherwise the same data read by the other
    // reader would show cards pointing at a deck that no longer exists.
    writeList(
      cardKeyFor(documentId),
      readList<StoredFlashcard>(cardKeyFor(documentId)).filter(
        (card) => card.deckId !== id
      )
    );
  },

  async getFlashcards(documentId: string): Promise<StoredFlashcard[]> {
    const deckIds = new Set(readDecks(documentId).map((deck) => deck.id));
    // An unknown deck falls back to the default rather than hiding the card,
    // matching how production repairs older data on read.
    return readList<StoredFlashcard>(cardKeyFor(documentId)).map((card) =>
      deckIds.has(card.deckId) ? card : { ...card, deckId: DEFAULT_DECK_ID }
    );
  },

  async addFlashcard(
    documentId: string,
    input: StoredFlashcardInput
  ): Promise<StoredFlashcard> {
    const card: StoredFlashcard = {
      id: mintId('fc'),
      createdAt: new Date().toISOString(),
      deckId: input.deckId || DEFAULT_DECK_ID,
      frontSide: input.frontSide,
      backSide: input.backSide,
      envelope: input.envelope,
    };
    writeList(cardKeyFor(documentId), [
      ...readList<StoredFlashcard>(cardKeyFor(documentId)),
      card,
    ]);
    return card;
  },

  async updateFlashcard(
    documentId: string,
    id: string,
    patch: StoredFlashcardPatch
  ): Promise<StoredFlashcard> {
    const cards = readList<StoredFlashcard>(cardKeyFor(documentId));
    const index = cards.findIndex((card) => card.id === id);
    if (index < 0) throw new Error(`No flashcard ${id} on ${documentId}`);

    const updated = { ...cards[index], ...patch };
    cards[index] = updated;
    writeList(cardKeyFor(documentId), cards);
    return updated;
  },

  async removeFlashcard(documentId: string, id: string): Promise<void> {
    writeList(
      cardKeyFor(documentId),
      readList<StoredFlashcard>(cardKeyFor(documentId)).filter(
        (card) => card.id !== id
      )
    );
  },

  async getBookmarks(documentId: string): Promise<StoredBookmark[]> {
    return readList<StoredBookmark>(bookmarkKeyFor(documentId));
  },

  async addBookmark(
    documentId: string,
    input: StoredBookmarkInput
  ): Promise<StoredBookmark> {
    const bookmark: StoredBookmark = {
      id: mintId('bm'),
      createdAt: new Date().toISOString(),
      envelope: input.envelope,
      label: input.label?.trim() || undefined,
    };
    writeList(bookmarkKeyFor(documentId), [
      ...readList<StoredBookmark>(bookmarkKeyFor(documentId)),
      bookmark,
    ]);
    return bookmark;
  },

  async removeBookmark(documentId: string, id: string): Promise<void> {
    writeList(
      bookmarkKeyFor(documentId),
      readList<StoredBookmark>(bookmarkKeyFor(documentId)).filter(
        (bookmark) => bookmark.id !== id
      )
    );
  },
};
